import { firebaseAction } from '../utils'
import { OWNER, EDITOR, COMMENTER, VIEWER, NO_ACCESS } from '../../constants'
import { accessListSelector } from '../../store/selectors/accessSelectors'
import ReactGA from 'react-ga'
import { replace } from 'react-router-redux'
import { STORE_SHARE } from '../actionTypes'
import { confirm } from './alerts'
import firebase from 'firebase/app'

const timestamp = firebase.database.ServerValue.TIMESTAMP

export const storeShare = share => ({
  type: STORE_SHARE,
  payload: { share }
})

export const shareReducer = (state = {}, { type, payload }) => {
  switch (type) {
    case STORE_SHARE:
      if (!payload.share) return { ...state, storedShare: null }
      return { ...state, storedShare: { stored: true, ...payload.share } }
    default:
      return state
  }
}

const levelStrings = {
  [OWNER]: 'Owner',
  [EDITOR]: 'Editor',
  [COMMENTER]: 'Commenter',
  [VIEWER]: 'Viewer',
  [NO_ACCESS]: 'No Access'
}

const makeRandomId = () =>
  (Math.floor(Math.random() * 15658734) + 1118481).toString(16).toUpperCase()

/**
 * Toggles share's disabled state
 * @param share
 */
export const toggleShare = share =>
  firebaseAction(update => {
    const disabled = !share.disabled // toggle
    const id = share.id || makeRandomId()
    const forward = {
      // write whole object because it might not exist yet
      [`/shares/${share.item.id}/${id}`]: {
        date: timestamp,
        id,
        item: share.item,
        accessLevel: share.accessLevel,
        multiUse: true,
        disabled
      }
    }
    if (share.accessLevel === VIEWER) {
      if (disabled) {
        forward[`/${share.item.type}s/${share.item.id}/viewLinkKey`] = null
      } else {
        forward[`/${share.item.type}s/${share.item.id}/viewLinkKey`] = id
      }
    }
    update({ forward })
    ReactGA.event(
      {
        category: 'Share',
        action: 'Update Link Access',
        label: levelStrings[share.accessLevel]
      },
      ['app']
    )
  })

export const inviteUsers = (item, users, level) =>
  firebaseAction((update, { getAuth }) => {
    const forward = {}
    users.forEach(({ uid, displayName, email }) => {
      const user = {}
      if (uid) user.id = uid
      if (displayName) user.displayName = displayName
      if (email) user.email = email

      const id = (Math.floor(Math.random() * 15658734) + 1118481)
        .toString(16)
        .toUpperCase()

      forward[`/shares/${item.id}/${id}`] = {
        id,
        date: timestamp,
        user,
        accessLevel: level,
        item: {
          name: item.name,
          type: item.type,
          id: item.id
        },
        sender: getAuth().displayName
      }
      ReactGA.event(
        {
          category: 'Share',
          action: 'Send Invite',
          label: levelStrings[level]
        },
        ['app']
      )
    })
    return update({ forward })
  })

// export function addMeToShareList(itemId) {
//   return firebaseAction(async (update, { getKey, getState, dispatch }) => {
//     const state = getState()
//     const access = accessListSelector(state)[itemId]
//     if (access && (access.isOwner || access.shareId || !access.accessLevel))
//       return
//     const user = userSelector(state)
//     if (user.isAnonymous) return

//     let forward = {}
//     const id = getKey()
//     forward[`/shares/${itemId}/${id}/id`] = id
//     forward[`/shares/${itemId}/${id}/date`] = ServerValue.TIMESTAMP
//     forward[`/shares/${itemId}/${id}/item`] = {
//       name: access.name,
//       type: access.type,
//       id: itemId
//     }
//     forward[`/shares/${itemId}/${id}/accessLevel`] = access.accessLevel
//     forward[`/shares/${itemId}/${id}/dateAccepted`] = ServerValue.TIMESTAMP
//     forward[`/shares/${itemId}/${id}/user`] = {
//       id: user.uid,
//       displayName: user.displayName,
//       email: user.email
//     }

//     forward[`/access/${user.uid}/${itemId}/shareId`] = id

//     await update({ forward })
//   })
// }

export const setShareLevel = ({ share, accessLevel }) =>
  firebaseAction(update => {
    let forward = {}

    if (accessLevel) {
      forward[`/shares/${share.item.id}/${share.id}/accessLevel`] = accessLevel
      ReactGA.event(
        {
          category: 'Share',
          action: 'Update Share Level',
          label: levelStrings[accessLevel]
        },
        ['app']
      )
    } else {
      forward[`/shares/${share.item.id}/${share.id}`] = null
      ReactGA.event(
        {
          category: 'Share',
          action: 'Revoke Share',
          label: share.dateAccepted ? 'accepted' : 'pending'
        },
        ['app']
      )
    }

    update({ forward })
  })

export const leaveShare = itemId =>
  firebaseAction((update, { getState, getAuth, dispatch }) => {
    const action = () => {
      const state = getState()
      const accessList = accessListSelector(state)
      const userId = getAuth().uid

      let forward = {}
      const leaveItem = itemId => {
        const access = accessList[itemId]
        forward[`/access/${userId}/${itemId}`] = null
        if (access.shareId)
          forward[`/shares/${itemId}/${access.shareId}`] = null

        // check for children
        Object.values(accessList || {}).forEach(({ id, parent }) => {
          if (parent === itemId) leaveItem(id)
        })

        // if we are currently on one any item we are leaving we need to navigate away
        if (window.location.pathname.includes(itemId)) dispatch(replace('/'))
      }
      leaveItem(itemId)

      update({ forward })

      ReactGA.event({ category: 'Share', action: 'Leave Share' }, ['app'])
    }

    dispatch(confirm('share.confirmLeave', action))
  })
